/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import FormCheckBoxController from 'commons/FormHook/FormCheckBoxController';
import IosSwitch from 'commons/IosSwitch';
import Text from 'commons/Text';

import EditArtworkSection from '../../../../commons/BoxSection';
import useStyles from './styles';

const EditArtworkCollectorComment = ({ artworkFormState }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { setValue, watch, control } = artworkFormState;
  const comments = watch('comments') || [];
  const currentUserId = useSelector(
    (state) => state.auth?.user?.id ?? state.auth?.account?.user?.id,
  );
  const [showPrevComments, setShowPrevComments] = useState(true);

  const formatDateTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const hh = d.getHours().toString().padStart(2, '0');
      const mm = d.getMinutes().toString().padStart(2, '0');
      const dd = d.getDate().toString().padStart(2, '0');
      const mon = (d.getMonth() + 1).toString().padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${hh}:${mm} ${dd}/${mon}/${yyyy}`;
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    if (!Array.isArray(comments) || !currentUserId) {
      return;
    }

    const mineList = comments.filter((c) => c?.user?.id === currentUserId);

    if (!mineList.length) {
      return;
    }

    const latest = mineList.sort(
      (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0),
    )[0];

    const current = watch('collector_comment');
    if (!current || current === (latest?.content || '')) {
      if (current !== (latest?.content || '')) {
        setValue('collector_comment', latest?.content || '');
      }
    }
  }, [comments, currentUserId]);

  return (
    <EditArtworkSection
      sx={{ mt: 2 }}
      heading={t('collectors_comment')}
    >
      <div className={classes.textareaWrap}>
        <textarea
          className={classes.textarea}
          maxLength={3000}
          value={watch('collector_comment') || ''}
          onChange={(e) => {
            setValue('collector_comment', e.target.value);
          }}
          aria-label={t('collectors_comment')}
        />
        <span className={classes.counter}>{watch('collector_comment')?.length || 0}/3000</span>
      </div>
      <div className={classes.checkboxRow}>
        <FormCheckBoxController
          name="show_collector_comment"
          control={control}
          textCheckBox={t('show_comment_on_detail_page')}
        />
      </div>
      {comments.length > 0 && (
        <>
          <div
            className={classes.prevHeader}
            onClick={() => setShowPrevComments((v) => !v)}
            role="button"
            tabIndex={0}
          >
            <Text
              type="md"
              fontWeightBold
            >
              {t('comments_from_previous_collectors')}
            </Text>
            <span className={classes.chevron}>{showPrevComments ? '▲' : '▼'}</span>
          </div>
          {showPrevComments && (
            <div className={classes.prevList}>
              {comments.map((c, idx) => (
                <div
                  key={idx}
                  className={classes.prevItem}
                >
                  <div className={classes.prevItemLeft}>
                    <div className={classes.prevMeta}>
                      <Text
                        type="md"
                        fontWeightBold
                      >
                        {t('collector')}
                      </Text>
                      <Text type="sm">{formatDateTime(c?.created_at)}</Text>
                    </div>
                  </div>
                  <div className={classes.prevItemRight}>
                    <IosSwitch
                      textSwitch={c?.is_hidden ? t('show') : t('hide')}
                      isChecked={!c?.is_hidden}
                      onSwitchChange={() => {
                        const next = [...comments];
                        next[idx] = { ...next[idx], is_hidden: !next[idx]?.is_hidden };
                        setValue('comments', next);
                      }}
                    />
                  </div>
                  <div className={classes.prevContent}>
                    <Text type="md">{c?.content}</Text>
                  </div>
                  {idx < comments.length - 1 && <div className={classes.divider} />}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </EditArtworkSection>
  );
};

export default EditArtworkCollectorComment;
